import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  AlertTriangleIcon,
  CheckIcon,
  CopyIcon,
  EyeIcon,
  EyeOffIcon,
  KeyRoundIcon,
  PlusIcon,
  ShieldCheckIcon,
  TerminalSquareIcon,
  Trash2Icon,
} from "lucide-react";

import {
  useApiKeys,
  useCreateApiKey,
  useProject,
  useRevokeApiKey,
} from "../app/queries";
import { ProjectTabs } from "../components/ProjectTabs";
import { Topbar } from "../components/Topbar";
import { ProjectNotFound } from "../components/ErrorState";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "../components/ui/alert";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Field,
  FieldDescription,
  FieldGroup,
  FieldLabel,
} from "../components/ui/field";
import { Input } from "../components/ui/input";
import { Separator } from "../components/ui/separator";
import { Skeleton } from "../components/ui/skeleton";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "../components/ui/tooltip";
import {
  Page,
  PageBody,
  PageDescription,
  PageHeader,
  PageTitle,
} from "../components/PageLayout";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { copyToClipboard } from "../lib/clipboard";
import { formatExact, shortDate } from "../lib/format";
import { cn } from "../lib/utils";
import type { ApiKeySummary } from "../lib/api";

export function ProjectApiKeysPage() {
  const { id = "" } = useParams();
  const projects = useProject(id);
  const apiKeys = useApiKeys(id);
  const createKey = useCreateApiKey(id, {
    success: (res) => `Minted key ${res.prefix}`,
    error: (err) => (err instanceof Error ? err.message : "Failed to mint key"),
    onSuccess: (res) => {
      setMinted({ token: res.token, prefix: res.prefix, name: res.name });
      setMintDialogOpen(false);
    },
  });
  const [revokeTargetName, setRevokeTargetName] = useState<string | null>(null);
  const revoke = useRevokeApiKey(id, {
    success: () => revokeTargetName ? `Revoked key ${revokeTargetName}` : "Key revoked",
    error: (err) => (err instanceof Error ? err.message : "Failed to revoke key"),
    onSuccess: () => {
      setRevokeTarget(null);
      setRevokeTargetName(null);
    },
  });

  const [minted, setMinted] = useState<{
    token: string;
    prefix: string;
    name: string;
  } | null>(null);
  const [mintDialogOpen, setMintDialogOpen] = useState(false);
  const [revokeTarget, setRevokeTarget] = useState<ApiKeySummary | null>(
    null,
  );
  const [acknowledged, setAcknowledged] = useState(false);

  const project = projects.project;
  const keys = apiKeys.data?.keys ?? [];

  if (projects.isLoading) {
    return (
      <Page>
        <Topbar title="…" />
        <PageBody width="wide">
          <Skeleton className="h-32 w-full" />
        </PageBody>
      </Page>
    );
  }

  if (!project) {
    return (
      <ProjectNotFound
        breadcrumb={[
          { label: "Projects", to: "/" },
          { label: "Not found" },
        ]}
      />
    );
  }

  return (
    <Page>
      <Topbar
        breadcrumb={[
          { label: "Projects", to: "/" },
          { label: project.name, to: `/projects/${id}` },
          { label: "API keys" },
        ]}
      />
      <ProjectTabs />
      <PageHeader>
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <PageTitle>API keys</PageTitle>
            <PageDescription>
              Mints and revokes credentials your game clients use to submit
              reports.
            </PageDescription>
          </div>
          <Button
            size="sm"
            onClick={() => setMintDialogOpen(true)}
            disabled={createKey.isPending}
            data-testid="open-mint-dialog"
          >
            <PlusIcon /> Mint API key
          </Button>
        </div>
      </PageHeader>
      <PageBody width="wide">
        <div className="grid grid-cols-1 gap-8 xl:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
          <div className="flex flex-col gap-8">
            {minted ? (
              <MintedKey
                keyValue={minted}
                acknowledged={acknowledged}
                onAcknowledge={() => setAcknowledged(true)}
                onDismiss={() => {
                  setMinted(null);
                  setAcknowledged(false);
                }}
              />
            ) : null}

            <Card>
              <CardHeader>
                <CardTitle>Active credentials</CardTitle>
                <CardDescription>
                  Each key authenticates requests to{" "}
                  <span className="font-mono">/api/v1/reports</span> using a
                  bearer token. Store the token securely — Mushi can only
                  show it once.
                </CardDescription>
              </CardHeader>
              <CardContent>
                {apiKeys.isLoading ? (
                  <Skeleton className="h-10 w-full" />
                ) : apiKeys.isError ? (
                  <Alert variant="destructive">
                    <AlertDescription>{apiKeys.error.message}</AlertDescription>
                  </Alert>
                ) : keys.length === 0 ? (
                  <div
                    data-testid="empty-keys"
                    className="flex flex-col items-start gap-2 py-6 text-sm text-muted-foreground"
                  >
                    <p>
                      No keys yet. Mint one to start ingesting reports from
                      this project.
                    </p>
                  </div>
                ) : (
                  <ApiKeyTable
                    keys={keys}
                    onRevoke={(k) => setRevokeTarget(k)}
                  />
                )}
              </CardContent>
            </Card>
          </div>

          <QuickStartSection
            projectKey={project.key}
            minted={minted}
            hasKeys={keys.length > 0}
          />
        </div>
      </PageBody>

      <MintDialog
        open={mintDialogOpen}
        busy={createKey.isPending}
        error={createKey.error?.message ?? null}
        onCancel={() => setMintDialogOpen(false)}
        onConfirm={async (name) => {
          try {
            await createKey.mutateAsync(name);
          } catch {
            // error toast is handled by useCreateApiKey
          }
        }}
      />
      <ConfirmDialog
        open={!!revokeTarget}
        title="Revoke API key"
        message={
          revokeTarget ? (
            <>
              Revoke the key <strong>{revokeTarget.name}</strong>? Game
              clients using this token will be rejected on their next
              request. This action cannot be undone.
            </>
          ) : (
            ""
          )
        }
        confirmLabel="Revoke key"
        destructive
        busy={revoke.isPending}
        onCancel={() => {
          setRevokeTarget(null);
          setRevokeTargetName(null);
        }}
        onConfirm={async () => {
          if (!revokeTarget) return;
          setRevokeTargetName(revokeTarget.name);
          try {
            await revoke.mutateAsync(revokeTarget.id);
          } catch {
            // error toast is handled by useRevokeApiKey
          }
        }}
      />
    </Page>
  );
}

function MintDialog({
  open,
  busy,
  error,
  onCancel,
  onConfirm,
}: {
  open: boolean;
  busy: boolean;
  error: string | null;
  onCancel: () => void;
  onConfirm: (name: string) => void;
}) {
  const [name, setName] = useState("");
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    if (open) {
      setName("");
      setTouched(false);
    }
  }, [open]);

  const trimmed = name.trim();
  const tooLong = trimmed.length > 80;
  const empty = trimmed.length === 0;
  const valid = !empty && !tooLong;
  const showError = touched && (empty || tooLong);

  return (
    <Dialog
      open={open}
      onOpenChange={(next: boolean) => {
        if (!next) onCancel();
      }}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <KeyRoundIcon /> Mint API key
          </DialogTitle>
          <DialogDescription>
            Give the key a meaningful name (e.g.{" "}
            <span className="font-mono">qa-build</span> or{" "}
            <span className="font-mono">steam-prod</span>). The token is
            shown only once after creation — copy it before closing this
            dialog.
          </DialogDescription>
        </DialogHeader>
        <form
          data-testid="mint-dialog"
          onSubmit={(e) => {
            e.preventDefault();
            setTouched(true);
            if (!valid) return;
            onConfirm(trimmed);
          }}
        >
          <FieldGroup>
            <Field data-invalid={showError || undefined}>
              <FieldLabel htmlFor="mint-name">Key name</FieldLabel>
              <Input
                id="mint-name"
                required
                maxLength={80}
                autoFocus
                placeholder="e.g. qa-build"
                value={name}
                onChange={(e) => setName(e.target.value)}
                onBlur={() => setTouched(true)}
                aria-invalid={showError || undefined}
                data-testid="mint-name"
                className="font-mono"
              />
              {showError ? (
                <FieldDescription
                  data-testid="mint-name-error"
                  className="text-destructive"
                >
                  {empty
                    ? "Key name is required."
                    : "Key name must be 80 characters or fewer."}
                </FieldDescription>
              ) : (
                <FieldDescription>
                  Letters, digits, dashes, underscores. Up to 80 characters.
                </FieldDescription>
              )}
            </Field>
            {error ? (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            ) : null}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={onCancel}>
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={busy}
                data-testid="confirm-mint"
                onClick={() => setTouched(true)}
              >
                {busy ? "Minting…" : "Mint key"}
              </Button>
            </DialogFooter>
          </FieldGroup>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function MintedKey({
  keyValue,
  acknowledged,
  onAcknowledge,
  onDismiss,
}: {
  keyValue: { token: string; prefix: string; name: string };
  acknowledged: boolean;
  onAcknowledge: (acknowledged: boolean) => void;
  onDismiss: () => void;
}) {
  const [revealed, setRevealed] = useState(false);
  const [copied, setCopied] = useState(false);
  const [copyError, setCopyError] = useState<string | null>(null);

  async function copy() {
    const result = await copyToClipboard(keyValue.token);
    if (result.ok) {
      setCopied(true);
      setCopyError(null);
      window.setTimeout(() => setCopied(false), 1500);
    } else {
      setCopyError(result.error);
    }
  }

  return (
    <Card data-testid="minted-key">
      <CardContent className="flex flex-col gap-4">
        <Alert variant="destructive" data-testid="minted-banner">
          <AlertTriangleIcon aria-hidden="true" />
          <AlertTitle>Copy this token now — it will not be shown again</AlertTitle>
          <AlertDescription>
            Anyone with this token can submit reports on behalf of{" "}
            <span className="font-mono">{keyValue.name}</span>. Store it in
            your secret manager or environment variables — never commit it
            to source control.
          </AlertDescription>
        </Alert>

        <div className="flex flex-wrap items-stretch gap-2">
          <code
            data-testid="minted-token"
            className={cn(
              "relative block min-w-0 flex-1 break-all rounded-md border border-border bg-muted px-3 py-2 font-mono text-xs text-foreground",
            )}
          >
            {revealed ? keyValue.token : maskToken(keyValue.token)}
          </code>
          <Tooltip>
            <TooltipTrigger
              render={
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => setRevealed((v) => !v)}
                  aria-label={revealed ? "Hide token" : "Reveal token"}
                  data-testid="reveal-minted-token"
                />
              }
            >
              {revealed ? <EyeOffIcon /> : <EyeIcon />}
              {revealed ? "Hide" : "Reveal"}
            </TooltipTrigger>
            <TooltipContent>
              {revealed
                ? "Conceal the token so the screen is safe to share."
                : "Show the full token. Treat what you see as a secret."}
            </TooltipContent>
          </Tooltip>
          <Button
            type="button"
            size="sm"
            onClick={copy}
            data-testid="copy-minted-token"
            className="shrink-0"
          >
            {copied ? (
              <>
                <CheckIcon /> Copied
              </>
            ) : (
              <>
                <CopyIcon /> Copy token
              </>
            )}
          </Button>
        </div>

        {copyError ? (
          <p className="text-xs text-destructive" role="status">
            {copyError}
          </p>
        ) : null}

        <Separator />

        <div className="flex flex-wrap items-center justify-between gap-3 text-xs text-muted-foreground">
          <span>
            Key prefix{" "}
            <span className="font-mono text-foreground">{keyValue.prefix}…</span>
            {" "}appears in audit logs.
          </span>
          <div className="flex items-center gap-2">
            <label className="inline-flex items-center gap-2">
              <input
                type="checkbox"
                className="size-4 rounded border-border accent-foreground"
                checked={acknowledged}
                onChange={(e) => onAcknowledge(e.target.checked)}
                data-testid="acknowledge-minted-token"
              />
              I have stored the token
            </label>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={onDismiss}
              disabled={!acknowledged}
              className="text-muted-foreground"
            >
              Dismiss
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function ApiKeyTable({
  keys,
  onRevoke,
}: {
  keys: ApiKeySummary[];
  onRevoke: (k: ApiKeySummary) => void;
}) {
  return (
    <ul data-testid="key-list" className="flex flex-col">
      {keys.map((key, idx) => (
        <li
          key={key.id}
          data-testid="key-row"
          className={cn(
            "grid grid-cols-1 gap-4 px-4 py-4 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-center",
            idx > 0 && "border-t border-border",
          )}
        >
          <div className="flex min-w-0 items-start gap-3">
            <span className="grid size-9 shrink-0 place-items-center rounded-md bg-muted text-muted-foreground">
              <KeyRoundIcon />
            </span>
            <div className="flex min-w-0 flex-col gap-1.5">
              <span className="truncate font-medium text-foreground">
                {key.name}
              </span>
              <code
                className="truncate font-mono text-xs text-muted-foreground"
                title={key.prefix}
              >
                {key.prefix}…
              </code>
              <dl className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
                <div className="flex items-center gap-1.5">
                  <dt className="text-muted-foreground">Scope</dt>
                  <ScopeBadge permissions={key.permissions} />
                </div>
                <div className="flex items-center gap-1.5">
                  <dt>Created</dt>
                  <dd title={formatExact(key.createdAt)}>
                    {shortDate(key.createdAt)}
                  </dd>
                </div>
                <div className="flex items-center gap-1.5">
                  <dt>Last used</dt>
                  <dd>
                    {key.lastUsedAt ? (
                      <span title={formatExact(key.lastUsedAt)}>
                        {shortDate(key.lastUsedAt)}
                      </span>
                    ) : (
                      <span className="text-muted-foreground">Never used</span>
                    )}
                  </dd>
                </div>
                {key.revokedAt ? (
                  <div className="flex items-center gap-1.5">
                    <dt>Revoked</dt>
                    <dd title={formatExact(key.revokedAt)}>
                      {shortDate(key.revokedAt)}
                    </dd>
                  </div>
                ) : null}
              </dl>
            </div>
          </div>
          <div className="flex items-center gap-2 sm:justify-end">
            <Badge
              variant="secondary"
              className={
                key.revokedAt
                  ? "text-muted-foreground"
                  : "border-success/30 bg-success/10 text-success"
              }
            >
              {key.revokedAt ? "revoked" : "active"}
            </Badge>
            {!key.revokedAt ? (
              <Button
                type="button"
                size="sm"
                variant="destructive"
                onClick={() => onRevoke(key)}
                data-testid="revoke-key"
              >
                <Trash2Icon /> Revoke
              </Button>
            ) : null}
          </div>
        </li>
      ))}
    </ul>
  );
}

function ScopeBadge({ permissions }: { permissions: string }) {
  const scopes = permissions
    .split(/[\s,]+/)
    .map((s) => s.trim())
    .filter(Boolean);
  if (scopes.length === 0) return null;
  return (
    <Tooltip>
      <TooltipTrigger
        render={
          <span
            className="inline-flex items-center gap-1 text-foreground/80"
            data-testid="key-scope"
          />
        }
      >
        <ShieldCheckIcon className="size-3 text-muted-foreground" />
        <span className="font-mono">{scopes.join(", ")}</span>
      </TooltipTrigger>
      <TooltipContent>{scopes.length} scope(s) granted</TooltipContent>
    </Tooltip>
  );
}

function QuickStartSection({
  projectKey,
  minted,
  hasKeys,
}: {
  projectKey: string;
  minted: { token: string; prefix: string; name: string } | null;
  hasKeys: boolean;
}) {
  return (
    <div className="flex flex-col gap-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TerminalSquareIcon className="size-4 text-muted-foreground" />
            Quick start
          </CardTitle>
          <CardDescription>
            A minimal integration for{" "}
            <span className="font-mono">{projectKey}</span>. Five steps and
            you have a report flowing into the dashboard.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-5">
          <Step
            n={1}
            title="Mint a key"
            description="Use the Mint API key button. Pick a name like qa-build or steam-prod."
            status={hasKeys ? "done" : "todo"}
          />
          <Step
            n={2}
            title="Store the token"
            description="Keep it in a secret manager or environment variable. Never commit it to source control."
            status={minted ? "todo" : "pending"}
          />
          <Step
            n={3}
            title="Configure base URL"
            description="Set MUSHI_BASE_URL to your server origin, for example http://localhost:8080."
            status="todo"
          />
          <Step
            n={4}
            title="Send a test report"
            description="POST a multipart request to /api/v1/reports using the example below."
            status="todo"
          />
          <Step
            n={5}
            title="Confirm it arrived"
            description="Open the project Reports page and verify the new entry shows up."
            status="todo"
          />
        </CardContent>
      </Card>

      <Card data-testid="curl-example">
        <CardHeader>
          <CardTitle>Send a test report</CardTitle>
          <CardDescription>
            Replace <span className="font-mono">$BASE</span> with your
            Mushi host and{" "}
            <span className="font-mono">$MUSHI_KEY</span> with a real
            token. The example below uses placeholders — never paste a
            live secret into shared docs.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          <CurlBlock />
        </CardContent>
      </Card>
    </div>
  );
}

function Step({
  n,
  title,
  description,
  status,
}: {
  n: number;
  title: string;
  description: string;
  status: "done" | "todo" | "pending";
}) {
  return (
    <div className="flex items-start gap-3">
      <span
        className={cn(
          "mt-0.5 grid size-6 shrink-0 place-items-center rounded-full text-xs font-semibold",
          status === "done"
            ? "bg-success text-success-foreground"
            : status === "pending"
              ? "border border-border text-muted-foreground"
              : "bg-foreground text-background",
        )}
        aria-hidden="true"
      >
        {n}
      </span>
      <div className="flex flex-col gap-0.5">
        <span className="text-sm font-medium text-foreground">{title}</span>
        <span className="text-sm text-muted-foreground">{description}</span>
      </div>
    </div>
  );
}

function CurlBlock() {
  const [copied, setCopied] = useState(false);
  const [copyError, setCopyError] = useState<string | null>(null);

  const fullCommand = [
    `# Endpoint`,
    `POST $BASE/api/v1/reports`,
    ``,
    `# Headers`,
    `Authorization: Bearer $MUSHI_KEY`,
    `Content-Type: multipart/form-data`,
    ``,
    `# Form fields`,
    `report={"externalId":"<your-id>","title":"hello","severity":"low","source":"player"}; type=application/json`,
    `files=@./shot.png`,
  ].join("\n");

  async function copy() {
    const result = await copyToClipboard(fullCommand);
    if (result.ok) {
      setCopied(true);
      setCopyError(null);
      window.setTimeout(() => setCopied(false), 1500);
    } else {
      setCopyError(result.error);
    }
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-end">
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={copy}
          data-testid="copy-curl"
        >
          {copied ? (
            <>
              <CheckIcon /> Copied
            </>
          ) : (
            <>
              <CopyIcon /> Copy example
            </>
          )}
        </Button>
      </div>
      {copyError ? (
        <p className="text-xs text-destructive" role="status">
          {copyError}
        </p>
      ) : null}
      <CurlSection title="Endpoint" code={`POST $BASE/api/v1/reports`} />
      <CurlSection
        title="Headers"
        code={`Authorization: Bearer $MUSHI_KEY
Content-Type: multipart/form-data`}
      />
      <CurlSection
        title="Form fields"
        code={`report={"externalId":"<your-id>","title":"hello","severity":"low","source":"player"}; type=application/json
files=@./shot.png`}
      />
      <p className="text-xs text-muted-foreground">
        <code className="font-mono">$MUSHI_KEY</code> is a placeholder —
        your real token was shown only when the key was minted.
      </p>
    </div>
  );
}

function CurlSection({ title, code }: { title: string; code: string }) {
  return (
    <div className="flex flex-col gap-1.5">
      <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
        {title}
      </span>
      <pre className="overflow-x-auto rounded-md border border-border bg-background px-3 py-2 font-mono text-xs leading-5 text-foreground">
        <code>{code}</code>
      </pre>
    </div>
  );
}

function maskToken(token: string): string {
  if (token.length <= 12) return "•".repeat(token.length);
  return `${token.slice(0, 6)}${"•".repeat(Math.max(8, token.length - 12))}${token.slice(-6)}`;
}
